import React from "react";
import { Link } from "react-router-dom";
import WaveDivider from "../components/WaveDivider";
import { Stethoscope, Wallet, Ear, HeartHandshake } from "lucide-react";
import "./Home.css";


const features = [
  {
    icon: Stethoscope,
    title: "Licensed Hearing Professionals",
    text: "Receive care from qualified audiologists and hearing care providers.",
  },
  {
    icon: Wallet,
    title: "Flexible Payment Options",
    text: "Eligible patients can access hearing aids through convenient payment plans.",
  },
  {
    icon: Ear,
    title: "Professionally Fitted Hearing Aids",
    text: "Every hearing aid recommendation begins with a professional hearing assessment.",
  },
  {
    icon: HeartHandshake,
    title: "Continuous Support",
    text: "We don't stop after fitting your hearing aids — we continue supporting your hearing journey.",
  },
];

const steps = [
  {
    n: "01",
    title: "Book a Hearing Assessment",
    text: "Meet with one of our licensed hearing professionals.",
  },
  {
    n: "02",
    title: "Receive Your Hearing Care Plan",
    text: "If a hearing aid is recommended, we'll explain your options.",
  },
  {
    n: "03",
    title: "Explore Flexible Payment Options",
    text: "Eligible patients can spread the cost through our financing programme.",
  },
  {
    n: "04",
    title: "Hear Better with Ongoing Support",
    text: "Enjoy professional fitting, follow-up care and continued support.",
  },
];

const audience = [
  "Adults experiencing hearing difficulties",
  "Existing hearing aid users",
  "Older adults",
  "Parents concerned about a child's hearing",
  "Individuals with tinnitus",
  "Anyone looking for professional hearing care",
];

export default function Home() {
  return (
    <>
      {/* Hero */}
      <section className="hero">
        <div className="container hero-inner">
          <span className="eyebrow" style={{ color: "var(--amber-500)" }}>HearMenders</span>
          <h1>Better Hearing.<br />Made More Accessible.</h1>
          <p className="hero-sub">
            HearMenders connects you with licensed hearing care professionals, professionally
            fitted hearing aids, flexible payment options, and ongoing support — all in one place.
          </p>
          <div className="hero-ctas">
            <Link to="/check-eligibility" className="btn btn-primary">Check Your Eligibility</Link>
            <Link to="/contact" className="btn btn-secondary">Book a Hearing Assessment</Link>
          </div>
        </div>
      </section>
      <WaveDivider tone="amber" />

      {/* Intro */}
      <section className="intro">
        <div className="container intro-inner">
          <h2>Better Hearing Starts with the Right Support</h2>
          <p>
            Whether you're struggling to hear conversations, looking for help for a loved one,
            or exploring hearing aid options, HearMenders is here to guide you every step of the way.
            We combine expert hearing care with innovative financing solutions to make hearing
            care more accessible than ever before.
          </p>
        </div>
      </section>

      {/* Why choose us */}
      <section className="features">
        <div className="container">
          <h2>Why Choose HearMenders?</h2>
          <div className="features-grid">
            {features.map((f) => (
              <div className="feature-card" key={f.title}>
                <f.icon className="feature-icon" size={32} strokeWidth={1.75} aria-hidden="true" />
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="process">
        <div className="container">
          <h2>How It Works</h2>
          <div className="process-grid">
            {steps.map((s) => (
              <div className="process-card" key={s.n}>
                <span className="process-n">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who can benefit */}
      <section className="audience">
        <div className="container">
          <h2>Who Can Benefit?</h2>
          <p>HearMenders is designed for:</p>
          <ul className="audience-list">
            {audience.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Financing teaser */}
      <section className="financing-teaser">
        <div className="container financing-teaser-inner">
          <h2>Hear Better Without the Financial Burden</h2>
          <p>
            One of the biggest reasons people delay getting hearing aids is affordability.
            HearMenders is changing that. If a hearing aid is recommended after your professional
            assessment, eligible patients can access flexible payment options through our
            financing partners. Because better hearing shouldn't have to wait.
          </p>
          <Link to="/financing" className="btn btn-outline">Learn About Financing</Link>
        </div>
      </section>

      {/* Community */}
      <section className="community">
        <div className="container">
          <h2>Hear From Our Community</h2>
          <div className="community-grid">
            <div className="community-card">
              <span className="eyebrow">Video Stories</span>
              <p>Real patients share how professional hearing care changed their everyday lives.</p>
            </div>
            <div className="community-card">
              <span className="eyebrow">Success Stories</span>
              <p>From first assessment to first fitting — journeys back to clear conversation.</p>
            </div>
            <div className="community-card">
              <span className="eyebrow">Patient Reviews</span>
              <p>Honest feedback from the people we support, before, during and after treatment.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <div className="container final-cta-inner">
          <h2>Ready to Hear Better?</h2>
          <p>Book your hearing assessment today.</p>
          <Link to="/contact" className="btn btn-primary">Book a Hearing Assessment</Link>
        </div>
      </section>
    </>
  );
}
